/**
 * HubEmailsPage — EMAIL-COMPOSER-SUPERADMIN (Tenon-3 restyle)
 *
 * Super-admin email template editor.
 * List: audience-tab strip + 5-col table.
 * Editor: 3-pane — HTML source (left) · preview (center) · variable picker (right 280px).
 * Save → PUT /hub/email-templates/{name}
 * Reset → DELETE /hub/email-templates/{name}
 * Audit log drawer → GET /hub/email-templates/{name}/log
 */
import { useEffect, useState, useCallback, useRef } from 'react'
import { Card, Pill, Icon } from './components'
import { IconButton } from '@/components/atoms/IconButton'
import {
  hubEmailTemplates,
  type HubEmailTemplateMeta,
  type HubEmailTemplateDetail,
  type HubEmailTemplateLogEntry,
} from './api'

const AUDIENCE_TABS = [
  { key: '',          label: 'All templates' },
  { key: 'client',    label: 'Client-facing' },
  { key: 'employee',  label: 'Internal · team' },
  { key: 'sub',       label: 'Subcontractor' },
  { key: 'app',       label: 'App / Tenant' },
]

export default function HubEmailsPage() {
  const [templates, setTemplates] = useState<HubEmailTemplateMeta[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [audienceTab, setAudienceTab] = useState('')

  const refresh = useCallback(async () => {
    setLoading(true)
    const r = await hubEmailTemplates.list().catch(() => null)
    if (r) setTemplates(r.items)
    setLoading(false)
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const filtered = audienceTab
    ? templates.filter(t => t.audience === audienceTab)
    : templates

  const audiencePill = (a: string) => {
    if (a === 'client')   return <Pill variant="info"  style={{ fontSize: 9 }}>CLIENT</Pill>
    if (a === 'employee') return <Pill variant="ok"    style={{ fontSize: 9 }}>INTERNAL</Pill>
    if (a === 'sub')      return <Pill variant="ghost" style={{ fontSize: 9 }}>SUB</Pill>
    if (a === 'app')      return <Pill variant="ghost" style={{ fontSize: 9 }}>APP</Pill>
    return <Pill variant="ghost" style={{ fontSize: 9 }}>{a.toUpperCase()}</Pill>
  }

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Card
        title={`Email templates · ${templates.length}`}
        endpoint="/hub/email-templates"
        bodyPad={false}
      >
        {/* Audience tab strip */}
        <div style={{
          display: 'flex',
          gap: 0,
          borderBottom: '1px solid var(--border)',
          padding: '0 14px',
          background: 'var(--surface-2)',
        }}>
          {AUDIENCE_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setAudienceTab(tab.key)}
              style={{
                padding: '9px 14px',
                fontSize: 11,
                fontWeight: audienceTab === tab.key ? 600 : 400,
                color: audienceTab === tab.key ? 'var(--brand-gold)' : 'var(--text-dim)',
                background: 'none',
                border: 'none',
                borderBottom: audienceTab === tab.key ? '2px solid var(--brand-gold)' : '2px solid transparent',
                cursor: 'pointer',
                letterSpacing: '0.02em',
                whiteSpace: 'nowrap',
                marginBottom: -1,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ padding: '20px', color: 'var(--text-dim)', fontSize: 12, textAlign: 'center' }}>
            Loading…
          </div>
        )}
        {!loading && (
          <table className="tbl">
            <thead>
              <tr>
                <th>Template · trigger</th>
                <th style={{ width: 240 }}>Default subject</th>
                <th style={{ width: 80 }}>Audience</th>
                <th style={{ width: 80 }}>Status</th>
                <th style={{ width: 100 }}>Last edited</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 24, color: 'var(--text-dim)', fontSize: 12 }}>
                    No templates in this category.
                  </td>
                </tr>
              )}
              {filtered.map(t => (
                <tr
                  key={t.name}
                  style={{ cursor: 'pointer', background: selected === t.name ? 'var(--row-hover)' : undefined }}
                  onClick={() => setSelected(t.name === selected ? null : t.name)}
                >
                  <td>
                    <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>{t.label}</div>
                    <div className="mono" style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 1 }}>{t.name}</div>
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--text-muted)' }}>—</td>
                  <td>{audiencePill(t.audience)}</td>
                  <td>
                    {t.has_override
                      ? <Pill variant="warn"  style={{ fontSize: 9 }}>CUSTOM</Pill>
                      : <Pill variant="ghost" style={{ fontSize: 9 }}>DEFAULT</Pill>}
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                    {t.has_override ? 'Customized' : 'Never edited'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <IconButton
                      aria-label="Edit template"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setSelected(t.name) }}
                    >
                      <Icon name="pencil" size={11} />
                    </IconButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {selected && (
        <TemplateEditor
          name={selected}
          onClose={() => setSelected(null)}
          onSaved={refresh}
        />
      )}
    </div>
  )
}

// ── Template editor ──────────────────────────────────────────────────────────

function TemplateEditor({
  name,
  onClose,
  onSaved,
}: {
  name: string
  onClose: () => void
  onSaved: () => void
}) {
  const [detail, setDetail]           = useState<HubEmailTemplateDetail | null>(null)
  const [draft, setDraft]             = useState<string>('')
  const [loading, setLoading]         = useState(true)
  const [saving, setSaving]           = useState(false)
  const [resetting, setResetting]     = useState(false)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewing, setPreviewing]   = useState(false)
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop')
  const [showLog, setShowLog]         = useState(false)
  const [log, setLog]                 = useState<HubEmailTemplateLogEntry[]>([])
  const [statusMsg, setStatusMsg]     = useState<string | null>(null)
  const [varSearch, setVarSearch]     = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const d = await hubEmailTemplates.get(name).catch(() => null)
    if (d) {
      setDetail(d)
      setDraft(d.override_html ?? d.default_html)
    }
    setLoading(false)
  }, [name])

  useEffect(() => { load() }, [load])

  const preview = async () => {
    if (!draft) return
    setPreviewing(true)
    const r = await hubEmailTemplates.preview(name, draft).catch(() => null)
    if (r) setPreviewHtml(r.html)
    setPreviewing(false)
  }

  const save = async () => {
    if (!draft) return
    setSaving(true)
    setStatusMsg(null)
    await hubEmailTemplates.put(name, draft).catch(() => null)
    setStatusMsg('Saved.')
    await load()
    onSaved()
    setSaving(false)
  }

  const reset = async () => {
    if (!detail) return
    setResetting(true)
    setStatusMsg(null)
    await hubEmailTemplates.delete(name).catch(() => null)
    setStatusMsg('Reset to default.')
    await load()
    onSaved()
    setResetting(false)
  }

  const loadLog = async () => {
    const r = await hubEmailTemplates.log(name).catch(() => null)
    if (r) setLog(r.items)
    setShowLog(true)
  }

  const isDirty = detail && draft !== (detail.override_html ?? detail.default_html)

  const insertAtCursor = (text: string) => {
    const el = textareaRef.current
    if (!el) return
    const s = el.selectionStart
    const e = el.selectionEnd
    const newVal = el.value.slice(0, s) + text + el.value.slice(e)
    setDraft(newVal)
    requestAnimationFrame(() => {
      el.focus()
      el.selectionStart = el.selectionEnd = s + text.length
    })
  }

  const variables = detail ? Object.entries(detail.stubs) : []
  const filteredVars = varSearch
    ? variables.filter(([k, v]) => k.toLowerCase().includes(varSearch.toLowerCase()) || v.toLowerCase().includes(varSearch.toLowerCase()))
    : variables

  if (loading) {
    return (
      <Card title={`Editing: ${name}`} bodyPad>
        <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Loading…</div>
      </Card>
    )
  }

  if (!detail) {
    return (
      <Card title={name} bodyPad>
        <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>Failed to load template.</div>
      </Card>
    )
  }

  return (
    <Card
      title=""
      bodyPad={false}
    >
      {/* Composer header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--surface-2)',
      }}>
        {/* Mail icon box */}
        <div style={{
          width: 36, height: 36,
          background: 'var(--surface-3, var(--surface-2))',
          border: '1px solid var(--border)',
          borderRadius: 6,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon name="mail" size={16} />
        </div>

        {/* Template name + status */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{detail.label}</span>
            {detail.has_override
              ? <Pill variant="warn"  style={{ fontSize: 9 }}>CUSTOMIZED</Pill>
              : <Pill variant="ghost" style={{ fontSize: 9 }}>DEFAULT</Pill>}
            {isDirty && <Pill variant="info" style={{ fontSize: 9 }}>UNSAVED</Pill>}
            <span className="mono" style={{ fontSize: 10, color: 'var(--text-dim)' }}>{name}</span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {statusMsg && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{statusMsg}</span>}
          <button
            className="btn ghost"
            style={{ fontSize: 11, padding: '3px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={() => { showLog ? setShowLog(false) : loadLog() }}
          >
            <Icon name="clock" size={10} />
            {showLog ? 'Hide log' : 'History'}
          </button>
          {detail.has_override && (
            <button
              className="btn ghost"
              style={{ fontSize: 11, padding: '3px 8px', display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-muted)' }}
              onClick={reset}
              disabled={resetting}
            >
              <Icon name="rotate-ccw" size={10} />
              {resetting ? 'Resetting…' : 'Reset to default'}
            </button>
          )}
          <button
            className="btn"
            style={{ fontSize: 11, padding: '3px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
            onClick={save}
            disabled={saving || !isDirty}
          >
            <Icon name="save" size={10} />
            {saving ? 'Saving…' : 'Save override'}
          </button>
          <IconButton aria-label="Close editor" size="sm" onClick={onClose}>
            <Icon name="x" size={12} />
          </IconButton>
        </div>
      </div>

      {/* 3-pane grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 280px', minHeight: 540 }}>

        {/* ── Left: HTML editor ── */}
        <div style={{ borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
          <div style={{
            padding: '6px 12px',
            borderBottom: '1px solid var(--border)',
            fontSize: 11,
            color: 'var(--text-dim)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'var(--surface-2)',
          }}>
            <Icon name="code" size={11} />
            <span>HTML source</span>
          </div>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            spellCheck={false}
            style={{
              flex: 1,
              width: '100%',
              border: 'none',
              outline: 'none',
              resize: 'none',
              fontFamily: 'monospace',
              fontSize: 11.5,
              lineHeight: 1.6,
              padding: '12px',
              background: 'var(--surface-1)',
              color: 'var(--text)',
              boxSizing: 'border-box',
            }}
          />
          {/* Footer */}
          <div style={{
            padding: '6px 12px',
            borderTop: '1px solid var(--border)',
            background: 'var(--surface-2)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: isDirty ? 'var(--brand-gold)' : 'var(--text-dim)' }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: isDirty ? 'var(--brand-gold)' : 'var(--text-dim)',
                display: 'inline-block',
              }} />
              {isDirty ? 'Unsaved changes' : 'Saved'}
            </div>
          </div>
        </div>

        {/* ── Center: preview ── */}
        <div style={{ borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
          {/* Desktop / Mobile toggle */}
          <div style={{
            padding: '6px 12px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--surface-2)',
          }}>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Icon name="eye" size={11} />
              <span>Preview</span>
            </div>
            <div style={{ display: 'flex', gap: 2, background: 'var(--surface-1)', borderRadius: 5, padding: 2, border: '1px solid var(--border)' }}>
              {(['desktop', 'mobile'] as const).map(m => (
                <button
                  key={m}
                  onClick={() => setPreviewMode(m)}
                  style={{
                    padding: '2px 10px', fontSize: 10, borderRadius: 4, border: 'none', cursor: 'pointer',
                    background: previewMode === m ? 'var(--surface-2)' : 'transparent',
                    color: previewMode === m ? 'var(--text)' : 'var(--text-dim)',
                    fontWeight: previewMode === m ? 600 : 400,
                  }}
                >
                  {m === 'desktop' ? 'Desktop' : 'Mobile'}
                </button>
              ))}
            </div>
            <button
              className="btn ghost"
              style={{ fontSize: 10, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: 3 }}
              onClick={preview}
              disabled={previewing}
            >
              <Icon name="refresh-cw" size={9} />
              {previewing ? 'Rendering…' : 'Refresh'}
            </button>
          </div>

          {/* Preview area */}
          <div style={{
            flex: 1,
            background: '#1a1a1a',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '20px 16px',
          }}>
            {previewHtml ? (
              <iframe
                key={previewMode}
                srcDoc={previewHtml}
                style={{
                  width: previewMode === 'mobile' ? 375 : '100%',
                  height: '100%',
                  minHeight: 440,
                  border: 'none',
                  background: '#fff',
                  borderRadius: 6,
                }}
                title="Email preview"
                sandbox="allow-same-origin"
              />
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                color: 'var(--text-dim)',
                fontSize: 12,
                minHeight: 300,
              }}>
                <Icon name="mail" size={28} />
                <span>Click "Refresh" to render preview</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Right: variable picker ── */}
        <div style={{ display: 'flex', flexDirection: 'column', background: 'var(--surface-2)' }}>
          <div style={{
            padding: '6px 12px',
            borderBottom: '1px solid var(--border)',
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--text)',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}>
            <Icon name="variable" size={11} />
            Variables
          </div>
          <div style={{ padding: '8px 10px 4px' }}>
            <input
              type="text"
              value={varSearch}
              onChange={e => setVarSearch(e.target.value)}
              placeholder="Search variables…"
              style={{
                width: '100%',
                padding: '5px 8px',
                fontSize: 11,
                background: 'var(--surface-1)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 5,
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '4px 10px 10px' }}>
            {variables.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--text-dim)', paddingTop: 8 }}>
                No variables for this template.
              </div>
            ) : filteredVars.length === 0 ? (
              <div style={{ fontSize: 11, color: 'var(--text-dim)', paddingTop: 8 }}>
                No matches.
              </div>
            ) : (
              filteredVars.map(([k, v]) => (
                <button
                  key={k}
                  onClick={() => insertAtCursor(`{{.${k}}}`)}
                  title={`Sample: ${v}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    width: '100%',
                    padding: '5px 8px',
                    margin: '2px 0',
                    background: 'none',
                    border: '1px solid transparent',
                    borderRadius: 4,
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--row-hover)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  <span className="mono" style={{ fontSize: 10, color: 'var(--brand-gold)' }}>{`{{.${k}}}`}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 1 }}>{v}</span>
                </button>
              ))
            )}
          </div>
          <div style={{ padding: '6px 10px', borderTop: '1px solid var(--border)', fontSize: 10, color: 'var(--text-dim)' }}>
            Click to insert at cursor
          </div>
        </div>
      </div>

      {showLog && <LogDrawer log={log} onClose={() => setShowLog(false)} />}
    </Card>
  )
}

// ── Audit log drawer ─────────────────────────────────────────────────────────

function LogDrawer({
  log,
  onClose,
}: {
  log: HubEmailTemplateLogEntry[]
  onClose: () => void
}) {
  return (
    <div style={{
      borderTop: '1px solid var(--border)',
      background: 'var(--surface-2)',
      padding: '12px 14px',
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Edit history</span>
        <IconButton aria-label="Close log" size="sm" onClick={onClose}>
          <Icon name="x" size={11} />
        </IconButton>
      </div>
      {log.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>No history yet.</div>
      )}
      {log.map(entry => (
        <div
          key={entry.id}
          className="row"
          style={{ gap: 8, padding: '5px 0', borderTop: '1px solid var(--border)' }}
        >
          <Pill variant={entry.action === 'reset' ? 'ghost' : 'warn'} style={{ fontSize: 9, minWidth: 42 }}>
            {entry.action.toUpperCase()}
          </Pill>
          <span style={{ fontSize: 11, color: 'var(--text-dim)', flex: 1 }}>
            {entry.admin_name || `Admin #${entry.admin_id}`}
          </span>
          <span className="mono" style={{ fontSize: 10, color: 'var(--text-muted)' }}>
            {relTime(entry.created_at)}
          </span>
        </div>
      ))}
    </div>
  )
}

// ── helpers ──────────────────────────────────────────────────────────────────

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (!isFinite(ms) || ms < 0) return '—'
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  return `${Math.floor(hr / 24)}d ago`
}
